import type { TranslationMemoryEntry } from '@src/repositories/TranslationMemoryEntryRepository';

/**
 * CSV column headers for the bulk export (PLAT-10873). They match the import
 * field schema (`source_term`/`translation`/`comment` are the columns the import
 * auto-maps, PLAT-10848) so an export round-trips back through the import flow;
 * `source_language`/`translation_language`/`collection` are carried along as
 * human-readable provenance.
 */
export const ENTRY_CSV_HEADERS = [
  'source_term',
  'source_language',
  'translation',
  'translation_language',
  'comment',
  'collection',
] as const;

// Quote a cell only when it contains a delimiter, quote, or newline; escape
// embedded quotes by doubling (RFC 4180).
const escapeCell = (value: string): string =>
  /[",\n\r]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value;

/**
 * Serialize entries to a CSV string with {@link ENTRY_CSV_HEADERS}. Language
 * links resolve to their BCP-47 `value`, an empty target language to a blank
 * cell, and the collection id to its display name (Default for uncategorized).
 */
export function entriesToCsv(
  entries: TranslationMemoryEntry[],
  resolveCollectionName: (collectionId: string | null) => string,
): string {
  const rows = entries.map(entry =>
    [
      entry.sourceTerm,
      entry.sourceLanguage?.value ?? '',
      entry.translation,
      entry.translationLanguage?.value ?? '',
      entry.comment ?? '',
      resolveCollectionName(entry.collectionId),
    ]
      .map(cell => escapeCell(String(cell ?? '')))
      .join(','),
  );
  return [ENTRY_CSV_HEADERS.join(','), ...rows].join('\r\n');
}

/**
 * Build the export file name `{collection}-{from}-to-{to}-{date}.csv`:
 * `collection` is the slugged collection name (falling back to "collection"),
 * `from`/`to` are the distinct source / target BCP-47 codes across the entries
 * ("multi" when they span more than one; a blank target language = "all"), and
 * `date` is the **local** calendar day (YYYY-MM-DD). `today` is injectable so
 * the date is testable without timezone flakiness.
 */
export function exportFileName(
  collectionName: string,
  entries: TranslationMemoryEntry[],
  today: Date = new Date(),
): string {
  const slug =
    collectionName
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '') || 'collection';
  const distinct = (
    pick: (e: TranslationMemoryEntry) => string | null | undefined,
    blank: string,
  ): string => {
    const values = new Set(entries.map(e => (pick(e) || blank).toLowerCase()));
    return values.size === 1 ? [...values][0] : 'multi';
  };
  const from = distinct(e => e.sourceLanguage?.value, 'unknown');
  const to = distinct(e => e.translationLanguage?.value, 'all');
  const pad = (n: number) => String(n).padStart(2, '0');
  const date = `${today.getFullYear()}-${pad(today.getMonth() + 1)}-${pad(
    today.getDate(),
  )}`;
  return `${slug}-${from}-to-${to}-${date}.csv`;
}

/** Trigger a browser download of a CSV string as a file. */
export function downloadCsv(filename: string, content: string): void {
  const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}
